// Clause search above the contents tree: a "/"-focusable field that lists matching clauses in document order with paging; a clause number in any letter case searches the number prefix in its stored form (falling back to full text when no number starts with it, as for standards such as G99), anything else searches full text, and in a document that is not in English words are searched across languages (hybrid search over the version, so an English query finds the original) with each hit's original and English snippet.
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { SearchIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import type { SearchHit } from "@/api/__generated__";
import {
  catalogGetDocumentOptions,
  clausesListClausesInfiniteOptions,
  searchSearchClausesOptions,
} from "@/api/__generated__/@tanstack/react-query.gen";
import { goSequencePending, isDialogOpen } from "@/app/shortcuts";
import { useInternalDetails } from "@/app/useInternalDetails";
import { BilingualText, isEnglish, MACHINE_TRANSLATION_NOTE } from "@/components/common/BilingualText";
import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Kbd } from "@/components/ui/kbd";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_DOT } from "@/features/library/format";
import { cn } from "@/lib/cn";
import { plural } from "@/lib/format";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 200;
const PAGE_SIZE = 50;
const CROSS_LANGUAGE_HITS = 30;
const SNIPPET_CHARS = 180;
const NUMBER_LIKE = /^(?:[A-Z][A-Z0-9]*(?:\.[A-Z0-9()]+)+\.?|[A-Z]{1,5}\d+[A-Z]?)$/iu;

type ClauseQuery = { q: string } | { number_prefix: string };

function storedNumber(value: string): string {
  const items = value.indexOf("(");
  return items < 0 ? value.toUpperCase() : `${value.slice(0, items).toUpperCase()}${value.slice(items)}`;
}

export function clauseQuery(text: string): ClauseQuery | null {
  const value = text.trim();
  if (value.length < MIN_QUERY) {
    return null;
  }
  return NUMBER_LIKE.test(value) ? { number_prefix: storedNumber(value) } : { q: value };
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

interface ClauseSearchProps {
  docUid: string;
  selectedUid: string | null;
  onSelect: (clauseUid: string) => void;
  children: ReactNode;
}

function snippet(text: string): string {
  const flat = text.replace(/\s+/gu, " ").trim();
  return flat.length > SNIPPET_CHARS ? `${flat.slice(0, SNIPPET_CHARS - 1).trimEnd()}…` : flat;
}

function CrossLanguageResults({
  docUid,
  text,
  selectedUid,
  onSelect,
}: {
  docUid: string;
  text: string;
  selectedUid: string | null;
  onSelect: (clauseUid: string) => void;
}) {
  const results = useQuery(
    searchSearchClausesOptions({ query: { q: text, doc_uid: [docUid], k: CROSS_LANGUAGE_HITS } }),
  );
  if (results.isError) {
    return (
      <ProblemState
        problem={results.error}
        compact
        retry={() => {
          void results.refetch();
        }}
        className="m-2"
      />
    );
  }
  if (results.isPending) {
    return (
      <div aria-hidden className="flex flex-col gap-2 p-3">
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} className="h-12" />
        ))}
      </div>
    );
  }
  const hits: SearchHit[] = results.data.hits;
  return (
    <div className="min-h-0 flex-1 pane-scroll">
      <p className="m-0 px-3 pt-2 pb-1 text-xs text-muted-foreground" role="status">
        {hits.length === 0
          ? "No clause matches."
          : `${plural(hits.length, "best match", "best matches")} in the original and its English translation`}
      </p>
      {results.data.notice ? (
        <p className="m-0 px-3 pb-1 text-xs text-muted-foreground">{results.data.notice}</p>
      ) : null}
      <ul className="m-0 list-none p-1.5" aria-label="Matching clauses">
        {hits.map((hit) => (
          <li key={hit.clause_uid}>
            <button
              type="button"
              aria-current={hit.clause_uid === selectedUid ? "true" : undefined}
              onClick={() => {
                onSelect(hit.clause_uid);
              }}
              className={cn(
                "flex w-full items-start gap-2 rounded-sm border-0 bg-transparent px-2 py-1.5 text-left text-xs hover:bg-accent",
                hit.clause_uid === selectedUid && "bg-selected text-selected-foreground hover:bg-selected",
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block font-mono">{hit.number}</span>
                {hit.title ? <span className="block truncate text-muted-foreground">{hit.title}</span> : null}
                {hit.text.trim() ? (
                  <BilingualText
                    variant="snippet"
                    original={snippet(hit.text)}
                    english={hit.snippet_en === null ? null : snippet(hit.snippet_en)}
                    language={hit.language}
                    className="mt-1 text-ink-2"
                  />
                ) : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {hits.some((hit) => hit.snippet_en !== null) ? (
        <p className="m-0 px-3 pb-3 text-label text-muted-foreground">{MACHINE_TRANSLATION_NOTE}</p>
      ) : null}
    </div>
  );
}

function Results({
  docUid,
  text,
  query,
  selectedUid,
  onSelect,
}: {
  docUid: string;
  text: string;
  query: ClauseQuery;
  selectedUid: string | null;
  onSelect: (clauseUid: string) => void;
}) {
  const internal = useInternalDetails();
  const request = { query: { doc_uid: docUid, limit: PAGE_SIZE, ...query } };
  const results = useInfiniteQuery({
    ...clausesListClausesInfiniteOptions(request),
    initialPageParam: request,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  if (results.isError) {
    return (
      <ProblemState
        problem={results.error}
        compact
        retry={() => {
          void results.refetch();
        }}
        className="m-2"
      />
    );
  }
  if (results.isPending) {
    return (
      <div aria-hidden className="flex flex-col gap-2 p-3">
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} className="h-9" />
        ))}
      </div>
    );
  }
  const items = results.data.pages.flatMap((page) => page.items);
  const total = results.data.pages[0]?.total ?? 0;
  if (total === 0 && "number_prefix" in query) {
    return (
      <Results
        docUid={docUid}
        text={text}
        query={{ q: text }}
        selectedUid={selectedUid}
        onSelect={onSelect}
      />
    );
  }
  return (
    <div className="min-h-0 flex-1 pane-scroll">
      <p className="m-0 px-3 pt-2 pb-1 text-xs text-muted-foreground" role="status">
        {total === 0 ? "No clause matches." : plural(total, "matching clause")}
      </p>
      <ul className="m-0 list-none p-1.5" aria-label="Matching clauses">
        {items.map((clause) => (
          <li key={clause.clause_uid}>
            <button
              type="button"
              aria-current={clause.clause_uid === selectedUid ? "true" : undefined}
              onClick={() => {
                onSelect(clause.clause_uid);
              }}
              className={cn(
                "flex w-full items-start gap-2 rounded-sm border-0 bg-transparent px-2 py-1.5 text-left text-ui hover:bg-accent",
                clause.clause_uid === selectedUid && "bg-selected text-selected-foreground hover:bg-selected",
              )}
            >
              {internal ? (
                <i
                  aria-hidden
                  className={cn("mt-1.5 size-2 shrink-0 rounded-full", STATUS_DOT[clause.extraction_status])}
                />
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block font-mono text-xs">{clause.number}</span>
                {clause.title ? (
                  <>
                    {" "}
                    <span className="block truncate text-xs text-muted-foreground">{clause.title}</span>
                  </>
                ) : null}
              </span>
              <span className="sr-only">, {plural(clause.requirement_count, "requirement")}</span>
              {clause.requirement_count > 0 ? (
                <span
                  aria-hidden
                  className="shrink-0 rounded-pill bg-muted px-1.5 text-label/[18px] font-semibold text-ink-2 tabular-nums"
                >
                  {clause.requirement_count}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {results.hasNextPage ? (
        <div className="px-3 pb-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={results.isFetchingNextPage}
            onClick={() => {
              void results.fetchNextPage();
            }}
          >
            {results.isFetchingNextPage ? "Loading…" : "Show more"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function ClauseSearch({ docUid, selectedUid, onSelect, children }: ClauseSearchProps) {
  const document = useQuery(catalogGetDocumentOptions({ path: { doc_uid: docUid } }));
  const crossLanguage = document.data !== undefined && !isEnglish(document.data.language);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebounced(text, DEBOUNCE_MS).trim();
  const query = clauseQuery(debounced);
  useHotkeys(
    "/",
    (event) => {
      if (goSequencePending()) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    },
    { useKey: true, ignoreEventWhen: isDialogOpen },
  );
  return (
    <>
      <div className="border-b p-2">
        <InputGroup className="h-8 bg-card">
          <InputGroupAddon>
            <SearchIcon aria-hidden />
          </InputGroupAddon>
          <InputGroupInput
            ref={inputRef}
            type="search"
            value={text}
            placeholder="Find a clause by number or text"
            aria-label="Find a clause by number or text"
            className="[&::-webkit-search-cancel-button]:hidden"
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && text.length > 0) {
                event.stopPropagation();
                setText("");
              }
            }}
          />
          <InputGroupAddon align="inline-end">
            {text.length > 0 ? (
              <button
                type="button"
                aria-label="Clear the clause search"
                className="grid size-6 place-items-center rounded-xs border-0 bg-transparent text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setText("");
                  inputRef.current?.focus();
                }}
              >
                <XIcon aria-hidden className="size-3.5" />
              </button>
            ) : (
              <Kbd>/</Kbd>
            )}
          </InputGroupAddon>
        </InputGroup>
      </div>
      {query === null ? (
        children
      ) : crossLanguage && "q" in query ? (
        <CrossLanguageResults docUid={docUid} text={query.q} selectedUid={selectedUid} onSelect={onSelect} />
      ) : (
        <Results
          docUid={docUid}
          text={debounced}
          query={query}
          selectedUid={selectedUid}
          onSelect={onSelect}
        />
      )}
    </>
  );
}
